<template>
  <div class="p-4 sm:p-6">
    <form @submit.prevent="submitExport" class="space-y-4">
      <p class="text-sm text-gray-600">Choose filters for the Excel export. Empty date range and status include all dates and all statuses.</p>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
        <select v-model="form.assignee_id"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          :disabled="loading">
          <option value="">All assignees</option>
          <option v-for="member in assigneeOptions" :key="member.id" :value="String(member.id)">
            {{ member.name }}{{ isCurrentUser(member) ? ' (You)' : '' }}
          </option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Date range (created)</label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">From</label>
            <input v-model="form.date_from" type="date"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              :disabled="loading" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">To</label>
            <input v-model="form.date_to" type="date"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              :disabled="loading" />
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
        <select v-model="form.status"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          :disabled="loading">
          <option value="">All statuses</option>
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="paused">Paused</option>
          <option value="in_review">In Review</option>
          <option value="done">Done</option>
        </select>
      </div>

      <p v-if="error" class="text-xs text-red-600">{{ error }}</p>

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" @click="handleClose"
          class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          :disabled="loading">
          Cancel
        </button>
        <button type="submit" :disabled="loading"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          <span v-if="loading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          <span>{{ loading ? 'Exporting...' : 'Export Excel' }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import gql from 'graphql-tag';

export default {
  name: 'export-tasks-modal',
  props: {
    boardId: {
      type: [Number, String],
      required: true,
    },
    boardMembers: {
      type: Array,
      default: function () {
        return [];
      },
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    currentUserName: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  data() {
    return {
      form: {
        assignee_id: '',
        date_from: '',
        date_to: '',
        status: '',
      },
      loading: false,
      error: null,
    };
  },
  computed: {
    assigneeOptions() {
      const members = Array.isArray(this.boardMembers) ? this.boardMembers : [];
      const list = [];
      const seen = {};
      for (let i = 0; i < members.length; i++) {
        const member = members[i];
        if (!member || member.id === undefined || member.id === null) {
          continue;
        }
        const idStr = String(member.id);
        if (seen[idStr]) {
          continue;
        }
        seen[idStr] = true;
        list.push(member);
      }
      if (this.currentUserId) {
        const uid = String(this.currentUserId);
        if (!seen[uid]) {
          list.unshift({
            id: this.currentUserId,
            name: this.currentUserName || 'Current user',
          });
        }
      }
      return list;
    },
  },
  created() {
    this.resetForm();
  },
  methods: {
    isCurrentUser(member) {
      if (!member || !this.currentUserId) {
        return false;
      }
      return parseInt(member.id, 10) === parseInt(this.currentUserId, 10);
    },
    resetForm() {
      this.form = {
        assignee_id: this.currentUserId ? String(this.currentUserId) : '',
        date_from: '',
        date_to: '',
        status: '',
      };
      this.error = null;
    },
    handleClose() {
      if (this.loading) {
        return;
      }
      this.$emit('close');
    },
    submitExport() {
      this.error = null;
      if (this.form.date_from && this.form.date_to && this.form.date_from > this.form.date_to) {
        this.error = 'Date from cannot be after date to.';
        return;
      }
      if (!this.boardId) {
        this.error = 'Board is not available.';
        return;
      }
      this.runExport();
    },
    async runExport() {
      this.loading = true;
      try {
        const result = await this.$apollo.mutate({
          mutation: gql`
            mutation ExportTaskBoard(
              $board_id: ID!
              $assignee_id: ID
              $date_from: Date
              $date_to: Date
              $status: String
            ) {
              exportTaskBoard(
                board_id: $board_id
                assignee_id: $assignee_id
                date_from: $date_from
                date_to: $date_to
                status: $status
              ) {
                error
                error_code
                message
                data {
                  download_url
                }
              }
            }
          `,
          variables: {
            board_id: String(this.boardId),
            assignee_id: this.form.assignee_id ? String(this.form.assignee_id) : null,
            date_from: this.form.date_from ? this.form.date_from : null,
            date_to: this.form.date_to ? this.form.date_to : null,
            status: this.form.status ? this.form.status : null,
          },
        });

        const payload = result && result.data && result.data.exportTaskBoard
          ? result.data.exportTaskBoard
          : null;

        if (!payload) {
          this.error = 'Failed to export tasks.';
          return;
        }
        if (payload.error) {
          this.error = payload.message || 'Failed to export tasks.';
          return;
        }

        const downloadUrl = payload.data && payload.data.download_url ? payload.data.download_url : null;
        if (!downloadUrl) {
          this.error = payload.message || 'No file was generated.';
          return;
        }

        const base = process.env.VUE_APP_PUBLIC_URL || '';
        const fullUrl = downloadUrl.indexOf('http') === 0 ? downloadUrl : base + downloadUrl;
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = 'tasks.xlsx';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (this.$swal) {
          this.$swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Excel export downloaded',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });
        }

        this.$emit('close');
      } catch (e) {
        this.error = (e && e.message) ? e.message : 'Failed to export tasks.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
